import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { weddingTemplates, defaultModulees, getRecommendedModulees } from '../data/weddingTemplates';
import PremiumPaywall from '../components/PremiumPaywall';
import CanvaHandoff from '../components/CanvaHandoff';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowRight, ArrowLeft, Sparkles, Heart, Calendar, MapPin, Users,
  Lock, Crown, CheckCircle2, Circle, GripVertical, Loader2, Eye,
  Download, Pencil, ExternalLink, BookOpen, ChevronRight, X,
  LayoutDashboard, CheckSquare, Calculator, Briefcase, Music,
  UtensilsCrossed, Wine, Camera, Video, Scissors, Shirt, Car,
  Hotel, Clock, ClipboardList, Baby, ShieldAlert, Plane, StickyNote,
  Flower2, Grid3x3, Palette, Type, ZoomIn,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import UserNavMenu from '../components/UserNavMenu';
import LumeraLogo from '../components/LumeraLogo';
import { PageMeta } from '../components/PageMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  LayoutDashboard, CheckSquare, Calculator, Users, Briefcase, MapPin,
  Heart, Music, UtensilsCrossed, Wine, Grid3x3, Sparkles, Flower2,
  Camera, Video, Scissors, Shirt, Car, Hotel, Clock, ClipboardList,
  Baby, ShieldAlert, Plane, StickyNote,
};
const getModuleeIcon = (iconName) => iconMap[iconName] || Circle;

// ═══════════════════════════════════════════════════════════
// COVER MOCKUPS — unique layout per template
// ═══════════════════════════════════════════════════════════
const TemplateCoverMockup = ({ template, size = 'large' }) => {
  const c = template.colors;
  const isLarge = size === 'large';
  const h = isLarge ? 'text-lg' : 'text-xs';
  const sub = isLarge ? 'text-[9px]' : 'text-[7px]';
  const detail = isLarge ? 'text-[8px]' : 'text-[6px]';

  const covers = {
    editorial: (
      <div className="relative h-full flex flex-col" style={{ fontFamily: template.fonts.body }}>
        <div className="absolute top-[8%] left-[10%] right-[10%] h-px" style={{ background: c.primary, opacity: 0.5 }} />
        <div className="absolute top-[10%] left-[10%] right-[10%] h-px" style={{ background: c.primary, opacity: 0.2 }} />
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <p className={`${detail} uppercase tracking-[4px] mb-4 font-semibold`} style={{ color: c.muted }}>Wedding Planner</p>
          <div className="w-6 h-px mb-3" style={{ background: c.primary }} />
          <h3 className={`${h} font-bold leading-tight`} style={{ color: c.text, fontFamily: template.fonts.heading }}>
            Emma<br/><span className={`${detail} italic font-normal`} style={{ color: c.muted }}>&</span><br/>Oliver
          </h3>
          <div className="w-6 h-px mt-3 mb-3" style={{ background: c.primary }} />
          <p className={`${detail} tracking-[2px]`} style={{ color: c.muted }}>June 2026 &middot; Tuscany</p>
        </div>
        <div className="absolute bottom-[10%] left-[10%] right-[10%] h-px" style={{ background: c.primary, opacity: 0.2 }} />
        <div className="absolute bottom-[8%] left-[10%] right-[10%] h-px" style={{ background: c.primary, opacity: 0.5 }} />
        {/* Botanical accent */}
        <svg className="absolute bottom-[14%] left-1/2 -translate-x-1/2 opacity-20" width="40" height="20" viewBox="0 0 40 20">
          <path d="M20 20 Q10 10 0 15 M20 20 Q15 8 10 0 M20 20 Q25 8 30 0 M20 20 Q30 10 40 15" stroke={c.primary} fill="none" strokeWidth="0.5"/>
        </svg>
      </div>
    ),
    minimal: (
      <div className="relative h-full flex flex-col p-[12%]" style={{ fontFamily: template.fonts.body }}>
        <div className="flex-1 flex flex-col justify-end">
          <p className={`${detail} tracking-[3px] uppercase mb-1`} style={{ color: c.muted }}>Wedding Planner</p>
          <h3 className={`${h} font-bold leading-none`} style={{ color: c.text, fontFamily: template.fonts.heading }}>
            Emma &<br/>Oliver
          </h3>
          <div className="flex items-center gap-1 mt-2">
            <div className="w-1 h-1 rounded-full" style={{ background: c.primary }} />
            <p className={`${detail} tracking-wider`} style={{ color: c.muted }}>2026</p>
          </div>
        </div>
        <div className="absolute bottom-[12%] right-[12%] w-[20%] aspect-square border" style={{ borderColor: c.border }} />
        <div className="absolute bottom-[14%] right-[14%] w-[18%] aspect-square border" style={{ borderColor: c.border, opacity: 0.4 }} />
      </div>
    ),
    flowing: (
      <div className="relative h-full overflow-hidden" style={{ fontFamily: template.fonts.body }}>
        <div className="absolute top-[5%] right-[-5%] w-[40%] aspect-square rounded-full" style={{ background: c.secondary, opacity: 0.12 }} />
        <div className="absolute top-[20%] left-[-10%] w-[30%] aspect-square rounded-full" style={{ background: c.primary, opacity: 0.1 }} />
        <div className="absolute bottom-[10%] right-[5%] w-[25%] aspect-square rounded-full" style={{ background: c.primary, opacity: 0.08 }} />
        <div className="relative h-full flex flex-col items-center justify-center px-6 text-center">
          <svg className="mb-3 opacity-30" width="30" height="15" viewBox="0 0 30 15">
            <ellipse cx="15" cy="7" rx="14" ry="6" stroke={c.primary} fill="none" strokeWidth="0.5"/>
            <path d="M8 7 Q11 2 15 7 Q19 12 22 7" stroke={c.secondary} fill="none" strokeWidth="0.5"/>
          </svg>
          <h3 className={`${h} font-bold leading-tight italic`} style={{ color: c.text, fontFamily: template.fonts.heading }}>
            Emma & Oliver
          </h3>
          <svg className="my-2" width="40" height="6" viewBox="0 0 40 6">
            <path d="M0 3 Q10 0 20 3 T40 3" stroke={c.primary} fill="none" strokeWidth="0.7" opacity="0.5"/>
          </svg>
          <p className={`${detail} tracking-[2px] uppercase`} style={{ color: c.muted }}>Wedding Planner</p>
          <p className={`${detail} mt-1`} style={{ color: c.muted }}>June 2026</p>
        </div>
      </div>
    ),
    grid: (
      <div className="relative h-full overflow-hidden" style={{ fontFamily: template.fonts.body }}>
        {/* Bold grid lines */}
        <div className="absolute top-0 left-[30%] w-[2px] h-full" style={{ background: c.primary, opacity: 0.08 }} />
        <div className="absolute top-[40%] left-0 w-full h-[2px]" style={{ background: c.primary, opacity: 0.08 }} />
        <div className="absolute top-[6%] left-[8%] right-[8%] flex items-center gap-2">
          <div className="w-[3px] h-5" style={{ background: c.primary }} />
          <span className={`${detail} uppercase tracking-[3px] font-bold`} style={{ color: c.accent }}>W.P.</span>
        </div>
        <div className="relative h-full flex flex-col justify-center pl-[12%] pr-[8%]">
          <h3 className={`${isLarge ? 'text-2xl' : 'text-base'} font-extrabold uppercase leading-none tracking-tight`}
            style={{ color: c.text, fontFamily: template.fonts.heading }}>
            Emma<br/>&amp;<br/>Oliver
          </h3>
          <div className="flex items-center gap-2 mt-3">
            <div className="h-px flex-1" style={{ background: c.primary }} />
            <span className={`${detail} font-bold tracking-wider`} style={{ color: c.accent }}>2026</span>
          </div>
        </div>
        {/* Grid squares */}
        <div className="absolute top-[8%] right-[8%] flex gap-1">
          {[0,1].map(i => <div key={i} className="w-2.5 h-2.5" style={{ background: c.primary, opacity: 0.15 + i * 0.1 }} />)}
        </div>
      </div>
    ),
    mediterranean: (
      <div className="relative h-full overflow-hidden" style={{ fontFamily: template.fonts.body }}>
        {/* Arch */}
        <div className="absolute top-[8%] left-[15%] right-[15%] aspect-[3/2]">
          <div className="w-full h-full rounded-t-full border-2" style={{ borderColor: c.primary, borderBottom: 'none', opacity: 0.25 }} />
        </div>
        <div className="relative h-full flex flex-col items-center justify-center px-6 text-center">
          <div className="mb-2">
            <svg width="20" height="12" viewBox="0 0 20 12" className="opacity-40">
              <ellipse cx="5" cy="6" rx="4" ry="5" fill={c.secondary} opacity="0.3"/>
              <ellipse cx="15" cy="6" rx="4" ry="5" fill={c.primary} opacity="0.3"/>
            </svg>
          </div>
          <h3 className={`${h} font-bold leading-tight`} style={{ color: c.text, fontFamily: template.fonts.heading }}>
            Emma & Oliver
          </h3>
          <div className="flex items-center gap-2 mt-2 mb-1">
            <div className="w-3 h-px" style={{ background: c.primary }} />
            <div className="w-1 h-1 rounded-full" style={{ background: c.primary }} />
            <div className="w-3 h-px" style={{ background: c.primary }} />
          </div>
          <p className={`${detail} tracking-[2px] uppercase`} style={{ color: c.muted }}>Wedding Planner</p>
          <p className={`${detail} mt-1 italic`} style={{ color: c.muted }}>Tuscany &middot; 2026</p>
        </div>
        {/* Tile pattern bottom */}
        <div className="absolute bottom-[5%] left-[20%] right-[20%] flex justify-center gap-0.5 opacity-15">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="w-2 h-2 rotate-45" style={{ background: i % 2 === 0 ? c.primary : c.secondary }} />
          ))}
        </div>
      </div>
    ),
    'dark-luxury': (
      <div className="relative h-full overflow-hidden" style={{ fontFamily: template.fonts.body }}>
        {/* Gold gradient lines */}
        <div className="absolute top-[8%] left-[10%] right-[10%] h-px" style={{ background: `linear-gradient(90deg, transparent, ${c.primary}, transparent)` }} />
        <div className="absolute top-[10%] left-[15%] right-[15%] h-px" style={{ background: `linear-gradient(90deg, transparent, ${c.primary}66, transparent)` }} />
        {/* Corner accents */}
        <div className="absolute top-[8%] left-[10%] w-3 h-3 border-l border-t" style={{ borderColor: c.primary }} />
        <div className="absolute top-[8%] right-[10%] w-3 h-3 border-r border-t" style={{ borderColor: c.primary }} />
        <div className="absolute bottom-[8%] left-[10%] w-3 h-3 border-l border-b" style={{ borderColor: c.primary }} />
        <div className="absolute bottom-[8%] right-[10%] w-3 h-3 border-r border-b" style={{ borderColor: c.primary }} />
        <div className="relative h-full flex flex-col items-center justify-center px-6 text-center">
          <div className="w-4 h-4 rotate-45 mb-3 border" style={{ borderColor: c.primary }} />
          <p className={`${detail} uppercase tracking-[4px] mb-3 font-semibold`} style={{ color: c.primary }}>Wedding Planner</p>
          <h3 className={`${h} font-bold leading-tight`} style={{ color: c.text, fontFamily: template.fonts.heading }}>
            Emma & Oliver
          </h3>
          <div className="flex items-center gap-1.5 mt-3">
            <div className="w-4 h-px" style={{ background: c.primary }} />
            <div className="w-1.5 h-1.5 rotate-45" style={{ background: c.primary }} />
            <div className="w-4 h-px" style={{ background: c.primary }} />
          </div>
          <p className={`${detail} mt-3 tracking-[2px]`} style={{ color: c.muted }}>June 2026</p>
        </div>
        <div className="absolute bottom-[10%] left-[15%] right-[15%] h-px" style={{ background: `linear-gradient(90deg, transparent, ${c.primary}66, transparent)` }} />
        <div className="absolute bottom-[8%] left-[10%] right-[10%] h-px" style={{ background: `linear-gradient(90deg, transparent, ${c.primary}, transparent)` }} />
      </div>
    ),
    ornate: (
      <div className="relative h-full overflow-hidden" style={{ fontFamily: template.fonts.body }}>
        {/* Outer frame */}
        <div className="absolute inset-[6%] border rounded-sm" style={{ borderColor: c.primary, opacity: 0.4 }} />
        {/* Inner frame */}
        <div className="absolute inset-[9%] border rounded-sm" style={{ borderColor: c.border }} />
        {/* Corner flourishes */}
        {['top-[6%] left-[6%]', 'top-[6%] right-[6%] rotate-90', 'bottom-[6%] right-[6%] rotate-180', 'bottom-[6%] left-[6%] -rotate-90'].map((pos, i) => (
          <div key={i} className={`absolute ${pos}`}>
            <svg width="12" height="12" viewBox="0 0 12 12">
              <path d="M0 0 Q6 0 6 6 M0 0 Q0 6 6 6" stroke={c.primary} fill="none" strokeWidth="0.6" opacity="0.5"/>
            </svg>
          </div>
        ))}
        <div className="relative h-full flex flex-col items-center justify-center px-[15%] text-center">
          <p className={`${detail} tracking-[3px] uppercase mb-3`} style={{ color: c.muted }}>Wedding Planner</p>
          <h3 className={`${h} font-bold leading-tight`} style={{ color: c.text, fontFamily: template.fonts.heading }}>
            Emma<br/>&amp; Oliver
          </h3>
          <svg className="my-2 opacity-40" width="30" height="6" viewBox="0 0 30 6">
            <path d="M0 3 L10 3 M20 3 L30 3" stroke={c.primary} strokeWidth="0.5"/>
            <circle cx="15" cy="3" r="2" stroke={c.primary} fill="none" strokeWidth="0.5"/>
          </svg>
          <p className={`${detail} italic`} style={{ color: c.muted }}>Tuscany &middot; June 2026</p>
        </div>
      </div>
    ),
    relaxed: (
      <div className="relative h-full overflow-hidden" style={{ fontFamily: template.fonts.body }}>
        {/* Soft horizontal lines */}
        <div className="absolute top-[15%] left-0 right-0 h-px" style={{ background: c.border }} />
        <div className="relative h-full flex flex-col items-center justify-center px-6 text-center">
          <h3 className={`${h} font-bold leading-tight`} style={{ color: c.text, fontFamily: template.fonts.heading }}>
            Emma & Oliver
          </h3>
          <p className={`${detail} tracking-[2px] uppercase mt-2 mb-1`} style={{ color: c.muted }}>Wedding Planner</p>
          <p className={`${detail}`} style={{ color: c.muted }}>June 2026 &middot; Amalfi</p>
        </div>
        {/* Wave decoration */}
        <svg className="absolute bottom-0 left-0 right-0 opacity-20" viewBox="0 0 200 30" preserveAspectRatio="none" style={{ height: '15%' }}>
          <path d="M0 15 Q12.5 5 25 15 T50 15 T75 15 T100 15 T125 15 T150 15 T175 15 T200 15 V30 H0Z" fill={c.primary} opacity="0.4"/>
          <path d="M0 20 Q12.5 10 25 20 T50 20 T75 20 T100 20 T125 20 T150 20 T175 20 T200 20 V30 H0Z" fill={c.secondary} opacity="0.3"/>
        </svg>
      </div>
    ),
  };

  return (
    <div data-testid={`cover-${template.id}`}
      className={`relative rounded-xl overflow-hidden shadow-lg ${isLarge ? 'aspect-[3/4]' : 'aspect-[3/4] w-full'}`}
      style={{ background: c.bg }}>
      {covers[template.coverStyle] || covers.editorial}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// INNER PAGE MOCKUPS — unique per template type
// ═══════════════════════════════════════════════════════════
const TemplateInnerMockup = ({ template, pageType = 'checklist' }) => {
  const c = template.colors;
  const titles = { checklist: 'Master Checklist', budget: 'Budget Overview', timeline: 'Day Timeline' };

  const renderContent = () => {
    const style = template.coverStyle;

    if (pageType === 'checklist') {
      if (style === 'grid' || style === 'dark-luxury') {
        return (
          <div className="space-y-1">
            {[85, 70, 90, 60, 75, 50].map((w, i) => (
              <div key={i} className="flex items-center gap-1">
                <div className="w-2.5 h-2.5 flex items-center justify-center flex-shrink-0" style={{ borderColor: c.border }}>
                  <div className={`w-2 h-0.5 ${i < 2 ? '' : 'opacity-0'}`} style={{ background: c.primary }} />
                  <div className={`absolute w-1.5 h-0.5 rotate-45 ${i < 2 ? '' : 'hidden'}`} style={{ background: c.primary }} />
                </div>
                <div className="h-1 rounded-full" style={{ background: c.border, width: `${w}%`, opacity: i < 2 ? 0.4 : 0.7 }} />
              </div>
            ))}
          </div>
        );
      }
      return (
        <div className="space-y-1.5">
          {[80, 65, 90, 55, 72, 48].map((w, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-sm border flex-shrink-0" style={{ borderColor: i < 2 ? c.primary : c.border, background: i < 2 ? c.primary + '20' : 'transparent' }}>
                {i < 2 && <div className="w-full h-full flex items-center justify-center"><div className="w-1 h-0.5" style={{ background: c.primary }} /></div>}
              </div>
              <div className="h-1.5 rounded-full" style={{ background: c.border, width: `${w}%` }} />
            </div>
          ))}
        </div>
      );
    }

    if (pageType === 'budget') {
      if (style === 'ornate' || style === 'mediterranean') {
        return (
          <div className="space-y-1.5">
            {['Venue', 'Catering', 'Photo', 'Flowers'].map((label, i) => (
              <div key={i}>
                <div className="flex justify-between items-center mb-0.5">
                  <div className="h-1 rounded-full" style={{ background: c.muted, width: '35%', opacity: 0.5 }} />
                  <div className="h-1 w-6 rounded-full" style={{ background: c.muted, opacity: 0.4 }} />
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: c.border }}>
                  <div className="h-full rounded-full" style={{ background: c.primary, width: `${70 - i * 12}%`, opacity: 0.6 }} />
                </div>
              </div>
            ))}
          </div>
        );
      }
      return (
        <div className="space-y-1">
          <div className="flex gap-1 mb-2">
            {[40, 25, 20, 15].map((w, i) => (
              <div key={i} className="rounded-sm" style={{ height: `${12 + w / 4}px`, width: `${w}%`, background: c.primary, opacity: 0.15 + i * 0.12 }} />
            ))}
          </div>
          {[70, 55, 85, 40].map((w, i) => (
            <div key={i} className="flex justify-between">
              <div className="h-1 rounded-full" style={{ background: c.border, width: `${w}%` }} />
              <div className="h-1 w-5 rounded-full" style={{ background: c.muted, opacity: 0.4 }} />
            </div>
          ))}
        </div>
      );
    }

    // timeline
    return (
      <div className="space-y-1.5">
        {['10:00', '12:30', '15:00', '18:00', '20:00'].map((time, i) => (
          <div key={i} className="flex items-start gap-1.5">
            <div className="flex flex-col items-center flex-shrink-0">
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: i === 1 ? c.primary : c.border }} />
              {i < 4 && <div className="w-px h-2" style={{ background: c.border }} />}
            </div>
            <div className="flex-1">
              <div className="h-1 w-4 rounded-full mb-0.5" style={{ background: c.muted, opacity: 0.5 }} />
              <div className="h-1 rounded-full" style={{ background: c.border, width: `${50 + Math.random() * 40}%` }} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="rounded-lg overflow-hidden shadow-md aspect-[3/4] w-full" style={{ background: c.surface, fontFamily: template.fonts.body }}>
      <div className="p-2.5 h-full flex flex-col">
        <div className="h-0.5 w-6 rounded mb-1.5" style={{ background: c.primary }} />
        <div className="text-[7px] font-bold mb-1.5 uppercase tracking-wider" style={{ color: c.accent, fontFamily: template.fonts.heading }}>
          {titles[pageType]}
        </div>
        <div className="flex-1">{renderContent()}</div>
        <div className="flex justify-between items-center mt-1.5 pt-1" style={{ borderTop: `0.5px solid ${c.border}` }}>
          <div className="h-0.5 w-8 rounded-full" style={{ background: c.border }} />
          <div className="text-[5px]" style={{ color: c.muted }}>{pageType === 'checklist' ? '04' : pageType === 'budget' ? '08' : '15'}</div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// COLOR PALETTE DISPLAY
// ═══════════════════════════════════════════════════════════
const ColorPalette = ({ template, compact = true }) => {
  const swatches = [
    { color: template.colors.bg, label: 'Bakgrunn' },
    { color: template.colors.primary, label: 'Primary' },
    { color: template.colors.secondary || template.colors.accent, label: 'Secondary' },
    { color: template.colors.text, label: 'Tekst' },
    { color: template.colors.accent, label: 'Aksent' },
  ];
  if (compact) {
    return (
      <div className="flex gap-1" data-testid={`palette-${template.id}`}>
        {swatches.slice(0, 4).map((s, i) => (
          <div key={i} className="w-3 h-3 rounded-full border border-white/50 shadow-sm" style={{ background: s.color }} />
        ))}
      </div>
    );
  }
  return (
    <div className="flex gap-3">
      {swatches.map((s, i) => (
        <div key={i} className="flex flex-col items-center gap-1">
          <div className="w-8 h-8 rounded-full shadow-sm border border-stone-200/50" style={{ background: s.color }} />
          <span className="text-[9px] text-stone-400 font-sans">{s.label}</span>
          <span className="text-[8px] text-stone-300 font-mono">{s.color}</span>
        </div>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// TEMPLATE DETAIL MODAL
// ═══════════════════════════════════════════════════════════
const TemplateDetailModal = ({ template, onClose, onSelect, canSelect }) => {
  if (!template) return null;
  const c = template.colors;
  return (
    <div data-testid="template-detail-modal" className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <button onClick={onClose} data-testid="modal-close-btn"
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors">
          <X className="w-4 h-4 text-stone-600" />
        </button>

        <div className="p-6 sm:p-8">
          {/* Header */}
          <div className="flex items-start gap-4 mb-6">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: c.primary + '20' }}>
              <Palette className="w-5 h-5" style={{ color: c.primary }} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl sm:text-2xl font-serif font-bold text-stone-900">{template.name}</h2>
                {template.isPro && (
                  <span className="flex items-center gap-1 bg-stone-900 text-white text-[9px] font-sans font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                    <Crown className="w-2.5 h-2.5" /> Pro
                  </span>
                )}
              </div>
              <p className="text-sm font-sans text-stone-500 mt-0.5">{template.description}</p>
            </div>
          </div>

          {/* Mockup grid: cover + 3 inner pages */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="col-span-1"><TemplateCoverMockup template={template} size="large" /></div>
            <div className="col-span-1"><TemplateInnerMockup template={template} pageType="checklist" /></div>
            <div className="col-span-1"><TemplateInnerMockup template={template} pageType="budget" /></div>
            <div className="col-span-1"><TemplateInnerMockup template={template} pageType="timeline" /></div>
          </div>

          {/* Typography + Color */}
          <div className="grid grid-cols-2 gap-6 mb-8">
            <div className="bg-stone-50 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Type className="w-4 h-4 text-stone-400" />
                <span className="text-xs font-sans font-semibold text-stone-600 uppercase tracking-wider">Typografi</span>
              </div>
              <p className="text-lg font-bold mb-1" style={{ fontFamily: template.fonts.heading, color: c.text }}>
                Heading Font
              </p>
              <p className="text-[11px] text-stone-400 font-mono mb-3">{template.fonts.heading.split("'")[1]}</p>
              <p className="text-sm leading-relaxed" style={{ fontFamily: template.fonts.body, color: c.muted }}>
                Body text med denne fonten ser slik ut. Elegant og lesbar for weddingplanneren din.
              </p>
              <p className="text-[11px] text-stone-400 font-mono mt-1">{template.fonts.body.split("'")[1]}</p>
            </div>
            <div className="bg-stone-50 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Palette className="w-4 h-4 text-stone-400" />
                <span className="text-xs font-sans font-semibold text-stone-600 uppercase tracking-wider">Fargepalett</span>
              </div>
              <ColorPalette template={template} compact={false} />
            </div>
          </div>

          {/* CTA */}
          <div className="flex items-center justify-center gap-3">
            {canSelect ? (
              <Button data-testid="modal-select-btn" onClick={() => { onSelect(template); onClose(); }}
                className="bg-[#B49A88] text-white hover:bg-[#B49A88]/85 shadow-xl hover:shadow-2xl transition-all duration-300 font-sans font-bold px-8 py-5 rounded-full text-base">
                Choose this design <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            ) : (
              <div className="text-center">
                <Button data-testid="modal-pro-btn" onClick={() => { onSelect(template); onClose(); }}
                  className="bg-stone-900 text-white hover:bg-stone-800 shadow-xl transition-all duration-300 font-sans font-bold px-8 py-5 rounded-full text-base mb-2">
                  <Crown className="w-4 h-4 mr-2 text-amber-400" /> Choose Pro design
                </Button>
                <p className="text-[11px] font-sans text-stone-400">Requires Pro or single purchase. You can continue testing after choosing.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// STEP 0: INTRO
// ═══════════════════════════════════════════════════════════
const WeddingIntro = ({ onStart }) => (
  <div data-testid="wedding-intro" className="min-h-[70vh] flex items-center justify-center">
    <div className="max-w-2xl text-center px-6">
      <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-gradient-to-br from-[#F5E6D8] to-[#E8D5C4] flex items-center justify-center">
        <Heart className="w-9 h-9 text-[#B49A88]" />
      </div>
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-stone-900 mb-4 leading-tight">
        Create your perfect<br />wedding planner
      </h1>
      <p className="text-base text-stone-500 font-sans mb-10 max-w-md mx-auto leading-relaxed">
        Lumera builds a complete, personal wedding planner based on your wishes and needs.
      </p>
      <div className="flex items-center justify-center gap-4 mb-10 flex-wrap">
        {['Choose design', 'Tell us about the wedding', 'Choose content', 'Lumera builds', 'Download'].map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-[#B49A88]/15 flex items-center justify-center text-xs font-sans font-bold text-[#B49A88]">{i + 1}</div>
            <span className="text-xs font-sans font-medium text-stone-600 hidden sm:inline">{s}</span>
            {i < 4 && <ChevronRight className="w-3.5 h-3.5 text-stone-300 hidden sm:inline" />}
          </div>
        ))}
      </div>
      <Button data-testid="wedding-start-btn" onClick={onStart}
        className="bg-[#B49A88] text-white hover:bg-[#B49A88]/85 shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 font-sans font-bold px-10 py-6 rounded-full text-base">
        Start <ArrowRight className="ml-2 w-4 h-4" />
      </Button>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════
// STEP 1: TEMPLATE GALLERY
// ═══════════════════════════════════════════════════════════
const WeddingTemplateGallery = ({ selected, onSelect, isPro, onDetail }) => (
  <div data-testid="wedding-template-gallery">
    <div className="text-center mb-10">
      <h2 className="text-2xl sm:text-3xl font-serif font-bold text-stone-900 mb-2">Choose your vibe</h2>
      <p className="text-sm font-sans text-stone-500">8 unike design. Klikk for a se detaljer. Hvert design former hele planneren din.</p>
    </div>

    {/* Free templates */}
    <div className="mb-8">
      <p className="text-xs font-sans font-semibold text-stone-400 uppercase tracking-wider mb-4">Free designs</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {weddingTemplates.filter(t => !t.isPro).map(t => (
          <TemplateCard key={t.id} template={t} isSelected={selected?.id === t.id} onSelect={onSelect} onDetail={onDetail} />
        ))}
      </div>
    </div>

    {/* Pro templates */}
    <div>
      <div className="flex items-center gap-2 mb-4">
        <p className="text-xs font-sans font-semibold text-stone-400 uppercase tracking-wider">Pro design</p>
        <Crown className="w-3.5 h-3.5 text-amber-500" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {weddingTemplates.filter(t => t.isPro).map(t => (
          <TemplateCard key={t.id} template={t} isSelected={selected?.id === t.id} onSelect={onSelect} onDetail={onDetail} isPro={!isPro} />
        ))}
      </div>
    </div>
  </div>
);

const TemplateCard = ({ template, isSelected, onSelect, onDetail, isPro }) => (
  <div data-testid={`template-${template.id}`}
    className={`relative group text-left rounded-2xl overflow-hidden transition-all duration-300 bg-white border ${
      isSelected
        ? 'ring-2 ring-[#B49A88] ring-offset-2 shadow-xl scale-[1.02] border-transparent'
        : 'border-stone-100 hover:shadow-lg hover:-translate-y-1'
    }`}>
    {/* Cover */}
    <div className="cursor-pointer" onClick={() => onDetail(template)}>
      <TemplateCoverMockup template={template} size="small" />
    </div>

    {/* Inner page previews */}
    <div className="grid grid-cols-3 gap-1 px-2 py-1.5 bg-stone-50/60">
      <TemplateInnerMockup template={template} pageType="checklist" />
      <TemplateInnerMockup template={template} pageType="budget" />
      <TemplateInnerMockup template={template} pageType="timeline" />
    </div>

    {/* Info bar */}
    <div className="p-3 flex items-start justify-between gap-2">
      <div className="min-w-0">
        <h3 className="font-serif font-bold text-stone-900 text-sm truncate">{template.name}</h3>
        <p className="text-[10px] font-sans text-stone-400 mt-0.5 truncate">{template.tagline}</p>
        <div className="mt-1.5"><ColorPalette template={template} /></div>
      </div>
      <button data-testid={`select-${template.id}`}
        onClick={() => onSelect(template)}
        className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all ${
          isSelected ? 'bg-[#B49A88] text-white shadow-md' : 'bg-stone-100 text-stone-400 hover:bg-stone-200'
        }`}>
        {isSelected ? <CheckCircle2 className="w-4 h-4" /> : <ArrowRight className="w-3.5 h-3.5" />}
      </button>
    </div>

    {/* PRO badge */}
    {template.isPro && (
      <div className="absolute top-2 right-2 flex items-center gap-1 bg-stone-900/80 backdrop-blur-sm text-white text-[9px] font-sans font-bold px-2 py-1 rounded-full uppercase tracking-wider">
        {isPro ? <Lock className="w-2.5 h-2.5" /> : <Crown className="w-2.5 h-2.5" />}
        Pro
      </div>
    )}

    {/* Expand button */}
    <button data-testid={`preview-${template.id}`}
      onClick={() => onDetail(template)}
      className="absolute top-2 left-2 w-6 h-6 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm">
      <ZoomIn className="w-3 h-3 text-stone-600" />
    </button>

    {isSelected && (
      <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-[#B49A88] flex items-center justify-center shadow-lg">
        <CheckCircle2 className="w-4 h-4 text-white" />
      </div>
    )}
  </div>
);

// ═══════════════════════════════════════════════════════════
// STEP 2: WEDDING PROFILE
// ═══════════════════════════════════════════════════════════
const WeddingProfileForm = ({ profile, onChange }) => {
  const update = (field, value) => onChange({ ...profile, [field]: value });
  const toggleField = (field) => onChange({ ...profile, [field]: !profile[field] });
  return (
    <div data-testid="wedding-profile-form">
      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl font-serif font-bold text-stone-900 mb-2">Tell us about the wedding</h2>
        <p className="text-sm font-sans text-stone-500">Jo mer vi vet, desto bedre blir planneren.</p>
      </div>
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-xs font-sans font-semibold text-stone-600 mb-1.5 block">Partner 1</Label>
            <Input data-testid="wp-partner1" value={profile.partner1Name} onChange={e => update('partner1Name', e.target.value)} placeholder="Navn" className="rounded-xl border-stone-200" /></div>
          <div><Label className="text-xs font-sans font-semibold text-stone-600 mb-1.5 block">Partner 2</Label>
            <Input data-testid="wp-partner2" value={profile.partner2Name} onChange={e => update('partner2Name', e.target.value)} placeholder="Navn" className="rounded-xl border-stone-200" /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-xs font-sans font-semibold text-stone-600 mb-1.5 block flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />Wedding date</Label>
            <Input data-testid="wp-date" type="date" value={profile.weddingDate} onChange={e => update('weddingDate', e.target.value)} className="rounded-xl border-stone-200" /></div>
          <div><Label className="text-xs font-sans font-semibold text-stone-600 mb-1.5 block flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />Venue</Label>
            <Input data-testid="wp-location" value={profile.location} onChange={e => update('location', e.target.value)} placeholder="By, land or venue" className="rounded-xl border-stone-200" /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-xs font-sans font-semibold text-stone-600 mb-1.5 block flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />Number of guests</Label>
            <Input data-testid="wp-guests" value={profile.guestCount} onChange={e => update('guestCount', e.target.value)} placeholder="Ca. antall" className="rounded-xl border-stone-200" /></div>
          <div><Label className="text-xs font-sans font-semibold text-stone-600 mb-1.5 block flex items-center gap-1.5"><Calculator className="w-3.5 h-3.5" />Budget</Label>
            <Input data-testid="wp-budget" value={profile.budget} onChange={e => update('budget', e.target.value)} placeholder="Total budget" className="rounded-xl border-stone-200" /></div>
        </div>
        <div>
          <Label className="text-xs font-sans font-semibold text-stone-600 mb-2 block">Bryllupsstil</Label>
          <div className="flex flex-wrap gap-2">
            {['Elegant', 'Rustic', 'Modern', 'Bohemian', 'Classic', 'Beach', 'Garden', 'Romantic'].map(style => (
              <button key={style} onClick={() => update('weddingStyle', style)} data-testid={`wp-style-${style.toLowerCase()}`}
                className={`px-3.5 py-1.5 rounded-full text-xs font-sans font-medium transition-all ${profile.weddingStyle === style ? 'bg-[#B49A88] text-white shadow-md' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
                {style}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-xs font-sans font-semibold text-stone-600 mb-1.5 block">Seremoni</Label>
            <select data-testid="wp-ceremony" value={profile.ceremonyType} onChange={e => update('ceremonyType', e.target.value)}
              className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm font-sans bg-white">
              <option value="">Select type</option>
              <option value="Church">Church</option>
              <option value="Civil">Civil</option>
              <option value="Utendors">Utendors</option>
              <option value="Symbolsk">Symbolsk</option>
              <option value="Other">Other</option>
            </select></div>
          <div><Label className="text-xs font-sans font-semibold text-stone-600 mb-1.5 block">Mottakelse</Label>
            <select data-testid="wp-reception" value={profile.receptionType} onChange={e => update('receptionType', e.target.value)}
              className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm font-sans bg-white">
              <option value="">Select type</option>
              <option value="Middag & fest">Middag & fest</option>
              <option value="Cocktailparty">Cocktailparty</option>
              <option value="Buffet">Buffet</option>
              <option value="Intimate dinner">Intimate dinner</option>
              <option value="Other">Other</option>
            </select></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[
            { key: 'childrenAttending', label: 'Child til stede' },
            { key: 'destinationWedding', label: 'Destination wedding' },
            { key: 'accommodationNeeded', label: 'Trenger overnatting' },
            { key: 'transportNeeded', label: 'Trenger transport' },
            { key: 'honeymoonPlanning', label: 'Honeymoon planning' },
          ].map(opt => (
            <button key={opt.key} onClick={() => toggleField(opt.key)} data-testid={`wp-toggle-${opt.key}`}
              className={`flex items-center gap-2.5 p-3 rounded-xl border text-left text-xs font-sans font-medium transition-all ${
                profile[opt.key] ? 'border-[#B49A88] bg-[#B49A88]/5 text-[#B49A88]' : 'border-stone-200 text-stone-500 hover:border-stone-300'
              }`}>
              <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${profile[opt.key] ? 'bg-[#B49A88] text-white' : 'bg-stone-100'}`}>
                {profile[opt.key] && <CheckCircle2 className="w-3 h-3" />}
              </div>
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// STEP 3: MODULE SELECTION
// ═══════════════════════════════════════════════════════════
const WeddingModuleeSelector = ({ modules, onToggle }) => (
  <div data-testid="wedding-module-selector">
    <div className="text-center mb-8">
      <h2 className="text-2xl sm:text-3xl font-serif font-bold text-stone-900 mb-2">Customize contentet</h2>
      <p className="text-sm font-sans text-stone-500">Choose the modules you want in your planner.</p>
    </div>
    <div className="max-w-2xl mx-auto space-y-2">
      {modules.map((mod) => {
        const Icon = getModuleeIcon(mod.icon);
        return (
          <div key={mod.id} data-testid={`module-${mod.id}`}
            className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
              mod.enabled ? 'border-[#B49A88]/30 bg-[#B49A88]/5' : 'border-stone-200 bg-white opacity-60'
            }`}>
            <GripVertical className="w-4 h-4 text-stone-300 cursor-grab flex-shrink-0" />
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${mod.enabled ? 'bg-[#B49A88]/15 text-[#B49A88]' : 'bg-stone-100 text-stone-400'}`}>
              <Icon className="w-4 h-4" />
            </div>
            <span className={`flex-1 text-sm font-sans font-medium ${mod.enabled ? 'text-stone-800' : 'text-stone-400'}`}>{mod.name}</span>
            <button onClick={() => onToggle(mod.id)} data-testid={`toggle-${mod.id}`}
              className={`w-10 h-5 rounded-full transition-all relative flex-shrink-0 ${mod.enabled ? 'bg-[#B49A88]' : 'bg-stone-200'}`}>
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${mod.enabled ? 'left-[22px]' : 'left-0.5'}`} />
            </button>
          </div>
        );
      })}
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════
// STEP 4: PREVIEW
// ═══════════════════════════════════════════════════════════
const WeddingPreview = ({ template, profile, modules, onGenerate, onChangeDesign }) => {
  const enabledModulees = modules.filter(m => m.enabled);
  return (
    <div data-testid="wedding-preview">
      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl font-serif font-bold text-stone-900 mb-2">Preview</h2>
        <p className="text-sm font-sans text-stone-500">See how your planner will look.</p>
      </div>
      <div className="max-w-3xl mx-auto">
        <div className="grid grid-cols-4 gap-3 mb-8">
          <TemplateCoverMockup template={template} size="large" />
          <TemplateInnerMockup template={template} pageType="checklist" />
          <TemplateInnerMockup template={template} pageType="budget" />
          <TemplateInnerMockup template={template} pageType="timeline" />
        </div>
        <div className="bg-white rounded-2xl border border-stone-100 p-6 space-y-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded-full" style={{ background: template.colors.primary }} />
            <span className="font-serif font-bold text-stone-900">{template.name}</span>
            <span className="text-xs font-sans text-stone-500">{template.tagline}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="px-2.5 py-1 bg-stone-50 rounded-full text-[11px] font-sans text-stone-600">
              {profile.partner1Name || '?'} & {profile.partner2Name || '?'}
            </span>
            {profile.weddingDate && <span className="px-2.5 py-1 bg-stone-50 rounded-full text-[11px] font-sans text-stone-600">{profile.weddingDate}</span>}
            {profile.location && <span className="px-2.5 py-1 bg-stone-50 rounded-full text-[11px] font-sans text-stone-600">{profile.location}</span>}
            {profile.guestCount && <span className="px-2.5 py-1 bg-stone-50 rounded-full text-[11px] font-sans text-stone-600">{profile.guestCount} guester</span>}
          </div>
          <div>
            <p className="text-xs font-sans font-semibold text-stone-600 mb-2">{enabledModulees.length} modules selected:</p>
            <div className="flex flex-wrap gap-1.5">
              {enabledModulees.map(m => (
                <span key={m.id} className="px-2 py-0.5 bg-[#B49A88]/10 text-[#B49A88] rounded-full text-[10px] font-sans font-medium">{m.name}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button data-testid="wedding-generate-btn" onClick={onGenerate}
            className="bg-[#B49A88] text-white hover:bg-[#B49A88]/85 shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 font-sans font-bold px-8 py-6 rounded-full text-base">
            <Sparkles className="w-4 h-4 mr-2" /> Create my planner
          </Button>
          <Button variant="outline" onClick={onChangeDesign}
            className="rounded-full px-6 py-5 font-sans font-medium border-stone-200 text-stone-600">
            Try a different design
          </Button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// STEP 5: GENERATING (real-time progress)
// ═══════════════════════════════════════════════════════════
const WeddingGenerating = ({ progress, modules }) => {
  const moduleNames = {};
  defaultModulees.forEach(m => { moduleNames[m.id] = m.name; });
  const fixedSteps = [
    { id: 'profile', label: 'Creating wedding profile' },
    ...modules.filter(m => m.enabled).map(m => ({ id: m.id, label: moduleNames[m.id] || m.name })),
    { id: 'styling', label: 'Styling your pages' },
  ];
  const doneSteps = new Set(progress.filter(p => p.status === 'done').map(p => p.step));
  const activeSteps = new Set(progress.filter(p => p.status === 'active').map(p => p.step));

  return (
    <div data-testid="wedding-generating" className="min-h-[60vh] flex items-center justify-center">
      <div className="max-w-md w-full text-center px-6">
        <Loader2 className="w-10 h-10 text-[#B49A88] animate-spin mx-auto mb-6" />
        <h2 className="text-2xl font-serif font-bold text-stone-900 mb-2">Building your wedding planner...</h2>
        <p className="text-sm font-sans text-stone-500 mb-8">Dette tar vanligvis 1-2 minutter.</p>
        <div className="text-left space-y-2">
          {fixedSteps.map(step => {
            const done = doneSteps.has(step.id);
            const active = activeSteps.has(step.id);
            return (
              <div key={step.id} className={`flex items-center gap-3 py-1.5 transition-opacity duration-300 ${done ? 'opacity-100' : active ? 'opacity-100' : 'opacity-40'}`}>
                {done ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" /> :
                 active ? <Loader2 className="w-4 h-4 text-[#B49A88] animate-spin flex-shrink-0" /> :
                 <Circle className="w-4 h-4 text-stone-300 flex-shrink-0" />}
                <span className={`text-sm font-sans ${done ? 'text-stone-700' : active ? 'text-stone-900 font-medium' : 'text-stone-400'}`}>{step.label}</span>
                {done && <span className="text-[10px] text-emerald-500 ml-auto font-sans">Done</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// STEP 6: RESULT + Canva prep
// ═══════════════════════════════════════════════════════════
const WeddingResult = ({ result, template, onDownload, onEdit }) => (
  <div data-testid="wedding-result" className="text-center">
    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-50 flex items-center justify-center">
      <CheckCircle2 className="w-8 h-8 text-emerald-500" />
    </div>
    <h2 className="text-2xl sm:text-3xl font-serif font-bold text-stone-900 mb-2">Your planner is ready!</h2>
    <p className="text-sm font-sans text-stone-500 mb-8">{result?.pages?.length || 0} sider generert med {template?.name} design.</p>
    <div className="max-w-lg mx-auto space-y-3 mb-8">
      <Button data-testid="wedding-edit-btn" onClick={onEdit}
        className="w-full bg-[#B49A88] text-white hover:bg-[#B49A88]/85 shadow-lg transition-all duration-300 font-sans font-semibold px-8 py-5 rounded-full text-base">
        <Pencil className="w-4 h-4 mr-2" /> Edit i Lumera
      </Button>
      <Button variant="outline" onClick={onDownload} data-testid="wedding-download-btn"
        className="w-full rounded-full py-5 font-sans font-semibold border-stone-200 text-stone-700">
        <Download className="w-4 h-4 mr-2" /> Download PDF
      </Button>
      {/* Canva Handoff — entitlement-gated */}
      <CanvaHandoff productSlug="wedding-planner" />
    </div>
  </div>
);


// ═══════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ═══════════════════════════════════════════════════════════
const WeddingPlannerPage = () => {
  const navigate = useNavigate();
  const { user, isPro, loading: authLoading, hasProductAccess, isEmailVerified } = useAuth();

  const [step, setStep] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [detailTemplate, setDetailTemplate] = useState(null);
  const [profile, setProfile] = useState({
    partner1Name: '', partner2Name: '', weddingDate: '', location: '',
    guestCount: '', budget: '', weddingStyle: '', ceremonyType: '',
    receptionType: '', childrenAttending: false, destinationWedding: false,
    accommodationNeeded: false, transportNeeded: false, honeymoonPlanning: false,
  });
  const [modules, setModulees] = useState(defaultModulees.map(m => ({ ...m, enabled: m.default })));
  const [showPaywall, setShowPaywall] = useState(false);
  const [previewAccess, setPreviewAccess] = useState(null);
  const [previewProduct, setPreviewProduct] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState([]);
  const [result, setResult] = useState(null);

  const autosaveTimer = useRef(null);
  const draftLoaded = useRef(false);

  const hasFull = isPro || hasProductAccess('wedding-planner');

  useEffect(() => { if (!authLoading && !user) navigate('/login?redirect=/wedding-planner'); }, [user, authLoading, navigate]);

  // Check access
  useEffect(() => {
    if (authLoading || !user) return;
    const check = async () => {
      try {
        const res = await axios.get(`${API}/purchases/preview-access/wedding-planner`);
        setPreviewAccess(res.data.access);
        setPreviewProduct(res.data.product);
      } catch { setPreviewAccess('full'); }
    };
    check();
  }, [authLoading, user]);

  // Load draft
  useEffect(() => {
    if (!user || draftLoaded.current || authLoading) return;
    draftLoaded.current = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/drafts/wedding-planner`);
        const d = res.data?.draft;
        if (d?.form_data && Object.keys(d.form_data).length > 1) {
          if (d.form_data._profile) setProfile(prev => ({ ...prev, ...d.form_data._profile }));
          let restoredTemplate = null;
          if (d.form_data._templateId) {
            const t = weddingTemplates.find(tp => tp.id === d.form_data._templateId);
            if (t) { setSelectedTemplate(t); restoredTemplate = t; }
          }
          if (d.form_data._modules) setModulees(d.form_data._modules);
          // Only restore step if template is present for steps that need it
          const restoredStep = d.step || 0;
          if (restoredStep >= 4 && !restoredTemplate) {
            setStep(1); // Fall back to gallery if template missing
          } else if (restoredStep > 0) {
            setStep(restoredStep);
          }
          toast('Welcome back', { description: 'We restored your saved progress.' });
        }
      } catch { /* no draft */ }
    };
    load();
  }, [user, authLoading]);

  // Autosave
  const saveDraft = useCallback(async () => {
    if (!user || step >= 5) return;
    try {
      await axios.post(`${API}/drafts/save`, {
        product_slug: 'wedding-planner', step,
        form_data: { _profile: profile, _templateId: selectedTemplate?.id, _modules: modules },
      });
    } catch { /* silent */ }
  }, [user, step, profile, selectedTemplate, modules]);

  useEffect(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(saveDraft, 2000);
    return () => clearTimeout(autosaveTimer.current);
  }, [profile, selectedTemplate, modules, step, saveDraft]);

  // Preview gating: allow steps 0-2 (intro, template, profile) for free
  const previewSteps = 3;
  const canProceed = useCallback((target) => {
    if (previewAccess === 'full') return true;
    return target <= previewSteps;
  }, [previewAccess]);

  const goToStep = useCallback((target) => {
    if (!canProceed(target)) { setShowPaywall(true); return; }
    setShowPaywall(false);
    setStep(target);
  }, [canProceed]);

  // Template selection: allow selecting ANY template (even Pro) for viewing
  // The paywall triggers when trying to GENERATE with a Pro template without access
  const handleTemplateSelect = (t) => {
    setSelectedTemplate(t);
  };

  const handleProfileChange = (newProfile) => {
    setProfile(newProfile);
    const rec = getRecommendedModulees(newProfile);
    setModulees(prev => prev.map(m => ({ ...m, enabled: rec.has(m.id) })));
  };

  const toggleModulee = (id) => {
    setModulees(prev => prev.map(m => m.id === id ? { ...m, enabled: !m.enabled } : m));
  };

  // Next button: check Pro template on step 1
  const handleNext = () => {
    if (step === 1) {
      if (!selectedTemplate) { toast.error('Choose a design first.'); return; }
      if (selectedTemplate.isPro && !hasFull) {
        setShowPaywall(true);
        return;
      }
    }
    goToStep(step + 1);
  };

  // Generate
  const handleGenerate = async () => {
    if (!selectedTemplate) { toast.error('Choose a design first.'); return; }
    if (selectedTemplate.isPro && !hasFull) { setShowPaywall(true); return; }
    setGenerating(true);
    setProgress([]);
    setStep(5);

    try {
      const res = await axios.post(`${API}/wedding-planner/generate`, {
        template_id: selectedTemplate.id,
        profile,
        modules: modules.filter(m => m.enabled).map(m => m.id),
        language: 'no',
        product_slug: 'wedding-planner',
      }, { timeout: 15000 });

      const taskId = res.data.task_id;
      let attempts = 0;
      while (attempts < 80) {
        await new Promise(r => setTimeout(r, 2500));
        attempts++;
        try {
          const poll = await axios.get(`${API}/content/tasks/${taskId}?_t=${Date.now()}`, { timeout: 10000 });
          if (poll.data.progress) setProgress(poll.data.progress);
          if (poll.data.status === 'complete') {
            setResult(poll.data.result);
            setStep(6);
            setGenerating(false);
            return;
          }
          if (poll.data.status === 'failed') {
            toast.error(poll.data.error || 'Generering feilet.');
            setGenerating(false);
            setStep(4);
            return;
          }
        } catch { /* continue polling */ }
      }
      toast.error('Generation took too long. Try again.');
      setGenerating(false);
      setStep(4);
    } catch (err) {
      if (err.response?.status === 403) {
        setShowPaywall(true);
        setStep(4);
      } else {
        toast.error('Something went wrong. Try again.');
        setStep(4);
      }
      setGenerating(false);
    }
  };

  if (authLoading || !user) return <div className="min-h-screen bg-[#FDFBF8] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#B49A88]" /></div>;

  return (
    <div className="min-h-screen bg-[#FDFBF8]">
      <PageMeta
        title="Wedding Planner"
        description="Complete digital wedding planner with budget, guest list, seating chart, timeline, vendors and more. AI-customized for your wedding."
        url="/wedding-planner"
      />
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FDFBF8]/95 backdrop-blur-md border-b border-stone-100/80">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <LumeraLogo size="sm" />
            <span className="text-sm font-serif font-bold text-stone-800">Wedding Planner</span>
          </div>
          <div className="flex items-center gap-4">
            {step > 0 && step < 5 && (
              <div className="hidden sm:flex items-center gap-1">
                {[1, 2, 3, 4].map(s => (
                  <button key={s} onClick={() => { if (s <= step) goToStep(s); }}
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-sans font-bold transition-all ${
                      s === step ? 'bg-[#B49A88] text-white shadow-md' :
                      s < step ? 'bg-[#B49A88]/20 text-[#B49A88]' : 'bg-stone-100 text-stone-400'
                    }`}>
                    {s < step ? <CheckCircle2 className="w-3 h-3" /> : s}
                  </button>
                ))}
              </div>
            )}
            {previewAccess === 'preview' && step > 0 && step < 5 && (
              <div className="flex items-center gap-1.5">
                <Eye className="w-3 h-3 text-amber-600" />
                <span className="text-[10px] font-sans font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                  Preview
                </span>
              </div>
            )}
            <UserNavMenu />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {showPaywall && (
          <div className="py-8">
            <PremiumPaywall product={previewProduct} productSlug="wedding-planner" />
          </div>
        )}

        {/* Template detail modal */}
        {detailTemplate && (
          <TemplateDetailModal
            template={detailTemplate}
            onClose={() => setDetailTemplate(null)}
            onSelect={handleTemplateSelect}
            canSelect={!detailTemplate.isPro || hasFull}
          />
        )}

        {!showPaywall && (
          <>
            {step === 0 && <WeddingIntro onStart={() => goToStep(1)} />}
            {step === 1 && (
              <WeddingTemplateGallery
                selected={selectedTemplate}
                onSelect={handleTemplateSelect}
                isPro={hasFull}
                onDetail={setDetailTemplate}
              />
            )}
            {step === 2 && <WeddingProfileForm profile={profile} onChange={handleProfileChange} />}
            {step === 3 && <WeddingModuleeSelector modules={modules} onToggle={toggleModulee} />}
            {step === 4 && selectedTemplate && <WeddingPreview template={selectedTemplate} profile={profile} modules={modules} onGenerate={handleGenerate} onChangeDesign={() => goToStep(1)} />}
            {step === 5 && <WeddingGenerating progress={progress} modules={modules} />}
            {step === 6 && result && <WeddingResult result={result} template={selectedTemplate} onEdit={() => navigate('/dashboard')} onDownload={() => toast('PDF-eksport kommer snart!')} />}
          </>
        )}

        {/* Navigation */}
        {!showPaywall && step >= 1 && step <= 4 && !generating && (
          <div className="flex items-center justify-between mt-10 pt-6 border-t border-stone-100">
            <Button variant="ghost" onClick={() => goToStep(step - 1)} className="font-sans font-medium text-stone-500">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            {step < 4 ? (
              <Button data-testid="wedding-next-btn" onClick={handleNext}
                className="bg-[#B49A88] text-white hover:bg-[#B49A88]/85 rounded-full px-6 py-5 font-sans font-semibold shadow-lg">
                Next <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            ) : null}
          </div>
        )}
      </main>
    </div>
  );
};

export default WeddingPlannerPage;
